import { Dispatch, SetStateAction } from 'react';
import Select from 'react-select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { useJobSetupModelForm } from '@/redux/post/usePostForm';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import ErrorMessage from '@/components/ErrorMessage';
import {
  IJobType,
  IUnits,
  IZZeroPosition,
  IXYDatumPosition,
  IToolSetter,
  IProbeForZHeightCommand,
  IPostUploadData,
  ISoftwareUsed,
  IMaterialUsed,
  GroupedEnum,
} from '@/redux/post/post.d'; // Adjust import path as needed
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  getGroupedOptionsFlat,
  getGroupedOptionsFromEnumAsGroups,
} from '@/redux/post/post.utils';

function JobSetupDetails({
  formData,
  errors,
  setFormData,
}: {
  formData: IPostUploadData;
  errors: { [key: string]: string };
  setFormData: Dispatch<SetStateAction<IPostUploadData>>;
}) {
  const jobSetupModelForm = useJobSetupModelForm(formData);

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (name: string, value: number | string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Job Setup Details"
        description="Enter job setup and machining details"
      />
      <div className="space-y-4">
        <Form {...jobSetupModelForm}>
          <form>
            <div className="flex flex-col py-2 gap-4 flex-wrap">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="softwareUsed"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Software Used</FormLabel>
                        <FormControl>
                          <Select
                            options={Object.values(ISoftwareUsed).map(
                              (option: string) => ({
                                label: option,
                                value: option,
                              })
                            )}
                            onChange={(selectedOption) => {
                              if (selectedOption?.value) {
                                handleSelectChange(
                                  field.name,
                                  selectedOption.value
                                );
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={{
                              label: Object.values(ISoftwareUsed).filter(
                                (model) => model === formData.softwareUsed
                              ) as unknown as string,
                              value: Object.values(ISoftwareUsed).filter(
                                (model) => model === formData.softwareUsed
                              ) as unknown as string,
                            }}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.softwareUsed} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="materialUsed"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Material Used</FormLabel>
                        <FormControl>
                          <Select
                            options={getGroupedOptionsFromEnumAsGroups(
                              IMaterialUsed as unknown as GroupedEnum
                            )}
                            onChange={(selectedOption) => {
                              if (selectedOption && selectedOption.label) {
                                handleSelectChange(
                                  field.name,
                                  selectedOption.label
                                );
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={{
                              label: getGroupedOptionsFlat(
                                IMaterialUsed as unknown as GroupedEnum
                              ).filter(
                                (model: any) => model === formData.materialUsed
                              ) as unknown as string,
                              value: getGroupedOptionsFlat(
                                IMaterialUsed as unknown as GroupedEnum
                              ).filter(
                                (model: any) => model === formData.materialUsed
                              ) as unknown as string,
                            }}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.materialUsed} />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="jobType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Job Type</FormLabel>
                        <FormControl>
                          <Select
                            options={Object.values(IJobType).map((option) => ({
                              label: option,
                              value: option,
                            }))}
                            onChange={(option) => {
                              if (option?.value) {
                                setFormData((prev: IPostUploadData) => {
                                  if (option.value === IJobType.ROTARY) {
                                    const {
                                      jobSizeWidth,
                                      jobSizeHeight,
                                      jobSizeThickness,
                                      ...rest
                                    } = prev;
                                    return {
                                      ...rest,
                                      jobType: option.value,
                                      jobSizeDiameter: 0.1,
                                      jobSizeLength: 0.1,
                                    } as IPostUploadData;
                                  }
                                  const {
                                    jobSizeDiameter,
                                    jobSizeLength,
                                    ...rest
                                  } = prev;
                                  return {
                                    ...rest,
                                    jobType: option.value,
                                    jobSizeWidth: 0.1,
                                    jobSizeHeight: 0.1,
                                    jobSizeThickness: 0.1,
                                  } as IPostUploadData;
                                });
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={{
                              label: Object.values(IJobType).filter(
                                (jobType) => jobType === formData.jobType
                              ) as unknown as string,
                              value: Object.values(IJobType).filter(
                                (jobType) => jobType === formData.jobType
                              ) as unknown as string,
                            }}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.jobSetupModel} />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="units"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Job Type Units</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) =>
                              handleSelectChange(field.name, value)
                            }
                            defaultValue={field.value || formData.units}
                            className="flex gap-4"
                          >
                            {Object.values(IUnits).map((option) => (
                              <button
                                type="button"
                                key={option}
                                className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                                onClick={() => field.onChange(option)}
                              >
                                <RadioGroupItem value={option} id={option} />
                                <label
                                  htmlFor={option}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                                >
                                  {option}
                                </label>
                              </button>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <ErrorMessage error={errors.units} />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {formData.jobType === IJobType.ROTARY ? (
                  <>
                    <div className="flex flex-col gap-2">
                      <FormField
                        control={jobSetupModelForm.control}
                        name="jobSizeDiameter"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Diameter in {formData.units}</FormLabel>
                            <FormControl>
                              <input
                                type="number"
                                value={formData.jobSizeDiameter}
                                onChange={(e) => {
                                  handleNumberChange(
                                    field.name,
                                    e.target.value
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value);
                                  field.onChange(value);
                                  handleNumberChange(field.name, value);
                                }}
                                className="w-full p-2 border rounded"
                              />
                            </FormControl>
                            <ErrorMessage error={errors.jobSizeDiameter} />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <FormField
                        control={jobSetupModelForm.control}
                        name="jobSizeLength"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Length in {formData.units}</FormLabel>
                            <FormControl>
                              <input
                                type="number"
                                value={formData.jobSizeLength}
                                onChange={(e) => {
                                  handleNumberChange(
                                    field.name,
                                    e.target.value
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value);
                                  field.onChange(value);
                                  handleNumberChange(field.name, value);
                                }}
                                className="w-full p-2 border rounded"
                              />
                            </FormControl>
                            <ErrorMessage error={errors.jobSizeLength} />
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex flex-col gap-2">
                      <FormField
                        control={jobSetupModelForm.control}
                        name="jobSizeWidth"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Job Size (X) in {formData.units}
                            </FormLabel>
                            <FormControl>
                              <input
                                type="number"
                                value={formData.jobSizeWidth}
                                onChange={(e) => {
                                  handleNumberChange(
                                    field.name,
                                    e.target.value
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value);
                                  field.onChange(value);
                                  handleNumberChange(field.name, value);
                                }}
                                className="w-full p-2 border rounded"
                              />
                            </FormControl>
                            <ErrorMessage error={errors.jobSizeWidth} />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <FormField
                        control={jobSetupModelForm.control}
                        name="jobSizeHeight"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Job Size (Y) in {formData.units}
                            </FormLabel>
                            <FormControl>
                              <input
                                type="number"
                                value={formData.jobSizeHeight}
                                onChange={(e) => {
                                  handleNumberChange(
                                    field.name,
                                    e.target.value
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value);
                                  field.onChange(value);
                                  handleNumberChange(field.name, value);
                                }}
                                className="w-full p-2 border rounded"
                              />
                            </FormControl>
                            <ErrorMessage error={errors.jobSizeHeight} />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <FormField
                        control={jobSetupModelForm.control}
                        name="jobSizeThickness"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Job Size (Z) in {formData.units}
                            </FormLabel>
                            <FormControl>
                              <input
                                type="number"
                                value={formData.jobSizeThickness}
                                onChange={(e) => {
                                  handleNumberChange(
                                    field.name,
                                    e.target.value
                                  );
                                }}
                                onBlur={(e) => {
                                  const value = parseFloat(e.target.value);
                                  field.onChange(value);
                                  handleNumberChange(field.name, value);
                                }}
                                className="w-full p-2 border rounded"
                              />
                            </FormControl>
                            <ErrorMessage error={errors.jobSizeThickness} />
                          </FormItem>
                        )}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="zZeroPosition"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Z Zero Position</FormLabel>
                        <FormControl>
                          <Select
                            options={Object.values(IZZeroPosition).map(
                              (option) => ({
                                label: option,
                                value: option,
                              })
                            )}
                            onChange={(option) => {
                              if (option?.value) {
                                handleSelectChange(field.name, option.value);
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={{
                              label: Object.values(IZZeroPosition).filter(
                                (position) =>
                                  position === formData.zZeroPosition
                              ) as unknown as string,
                              value: Object.values(IZZeroPosition).filter(
                                (position) =>
                                  position === formData.zZeroPosition
                              ) as unknown as string,
                            }}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.zZeroPosition} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="xyDatumPosition"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>XY Datum Position</FormLabel>
                        <FormControl>
                          <Select
                            options={Object.values(IXYDatumPosition).map(
                              (option) => ({
                                label: option,
                                value: option,
                              })
                            )}
                            onChange={(option) => {
                              if (option?.value) {
                                handleSelectChange(field.name, option.value);
                              }
                            }}
                            isSearchable
                            name={field.name}
                            value={{
                              label: Object.values(IXYDatumPosition).filter(
                                (position) =>
                                  position === formData.xyDatumPosition
                              ) as unknown as string,
                              value: Object.values(IXYDatumPosition).filter(
                                (position) =>
                                  position === formData.xyDatumPosition
                              ) as unknown as string,
                            }}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.xyDatumPosition} />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="toolSetter"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tool Setter</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) =>
                              handleSelectChange(field.name, value)
                            }
                            defaultValue={field.value || formData.toolSetter}
                            className="flex gap-4"
                          >
                            {Object.values(IToolSetter).map((option) => (
                              <button
                                type="button"
                                key={option}
                                className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                                onClick={() => field.onChange(option)}
                              >
                                <RadioGroupItem value={option} id={option} />
                                <label
                                  htmlFor={option}
                                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                                >
                                  {option}
                                </label>
                              </button>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <ErrorMessage error={errors.toolSetter} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <FormField
                    control={jobSetupModelForm.control}
                    name="probeForZHeightCommand"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Probe for Z Height Command</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) =>
                              handleSelectChange(field.name, value)
                            }
                            defaultValue={
                              field.value || formData.probeForZHeightCommand
                            }
                            className="flex gap-4"
                          >
                            {Object.values(IProbeForZHeightCommand).map(
                              (option) => (
                                <button
                                  type="button"
                                  key={option}
                                  className="flex items-center space-x-2 w-full border border-[#D6D6D6] h-[40px] p-2 rounded-sm cursor-pointer"
                                  onClick={() => field.onChange(option)}
                                >
                                  <RadioGroupItem value={option} id={option} />
                                  <label
                                    htmlFor={option}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1"
                                  >
                                    {option}
                                  </label>
                                </button>
                              )
                            )}
                          </RadioGroup>
                        </FormControl>
                        <ErrorMessage error={errors.probeForZHeightCommand} />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <FormField
                  control={jobSetupModelForm.control}
                  name="spindleSpeed"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Spindle Speed (RPM)</FormLabel>
                      <FormControl>
                        <input
                          type="number"
                          min="6000"
                          max="24000"
                          value={field.value}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            field.onChange(value);
                            handleNumberChange(field.name, value);
                          }}
                          className="w-full p-2 border rounded"
                        />
                      </FormControl>
                      <ErrorMessage error={errors.spindleSpeed} />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default JobSetupDetails;
